import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import {
  buildDailyTotalsSql, buildUserChurnSql, buildCompanySql, USER_ROSTER_SQL,
  mapDailyTotalRows, mapUserChurnRows, mapCompanyRows, mapUserRosterRows,
} from "./sql";
import type { DailyTotalRow, UserChurnRow, CompanyRow, UserRosterRow } from "./sql";
import {
  bucketize, buildCompanyIndicators, buildUserIndicators, computeKpisFromUserChurn,
  addDays, toISODate, fmtN, rankLevel,
} from "./helpers";
import type { BucketSeries, CompanyIndicator, UserIndicator } from "./helpers";
import { PageFiltersBar, TableFilterBar, pageFiltersToPills } from "./filters";
import type { PageFilters, TableFilters } from "./filters";
import { SectionCardHeader } from "../../components/ChartCard";
import {
  KpiCard, MeetingsFunnelChart, EngagementChart, FunnelTable, EngagementTable,
  StatusBadge, RiskBadge, TierBadge,
} from "./charts";
import { LoadingPanel } from "../../components/LoadingPanel";

function defaultPageFilters(today: Date): PageFilters {
  return {
    rangeDays: 90,
    bucket: "weekly",
    graphStart: toISODate(addDays(today, -60)),
    graphEnd: toISODate(today),
  };
}

function initialsFor(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? "";
  return (first + second).toUpperCase() || "?";
}

export function ChurnLeadingIndicators() {
  const today = useMemo(() => new Date(), []);
  const [filters, setFilters] = useState<PageFilters>(() => defaultPageFilters(today));
  const [companyTable, setCompanyTable] = useState<TableFilters>({ paidOnly: false, selectedKey: null, country: null });
  const [userTable, setUserTable] = useState<TableFilters>({ paidOnly: false, selectedKey: null, country: null });

  const dailyTotalsSql = useMemo(() => buildDailyTotalsSql(filters.rangeDays), [filters.rangeDays]);
  const userChurnSql   = useMemo(() => buildUserChurnSql(), []);
  const companySql     = useMemo(() => buildCompanySql(filters.rangeDays), [filters.rangeDays]);

  const dailyResult     = useAdminQuery(dailyTotalsSql);
  const userChurnResult = useAdminQuery(userChurnSql);
  const companyResult   = useAdminQuery(companySql);
  const rosterResult    = useAdminQuery(USER_ROSTER_SQL);

  const dailyRows = useMemo<DailyTotalRow[]>(() => {
    if (!dailyResult.data) return [];
    return mapDailyTotalRows(dailyResult.data.columns, dailyResult.data.rows as unknown[][]);
  }, [dailyResult.data]);

  const userChurnRows = useMemo<UserChurnRow[]>(() => {
    if (!userChurnResult.data) return [];
    return mapUserChurnRows(userChurnResult.data.columns, userChurnResult.data.rows as unknown[][]);
  }, [userChurnResult.data]);

  const companyRows = useMemo<CompanyRow[]>(() => {
    if (!companyResult.data) return [];
    return mapCompanyRows(companyResult.data.columns, companyResult.data.rows as unknown[][]);
  }, [companyResult.data]);

  const rosterRows = useMemo<UserRosterRow[]>(() => {
    if (!rosterResult.data) return [];
    return mapUserRosterRows(rosterResult.data.columns, rosterResult.data.rows as unknown[][]);
  }, [rosterResult.data]);

  const isLoading =
    dailyResult.isLoading || userChurnResult.isLoading || companyResult.isLoading || rosterResult.isLoading;
  const error =
    (dailyResult.error as Error | null) ??
    (userChurnResult.error as Error | null) ??
    (companyResult.error as Error | null) ??
    (rosterResult.error as Error | null);

  const graphRows = useMemo(() => {
    const start = filters.graphStart;
    const end = filters.graphEnd;
    return dailyRows.filter((r) => r.day >= start && r.day <= end);
  }, [dailyRows, filters.graphStart, filters.graphEnd]);

  const funnelSeries: BucketSeries[] = useMemo(() => bucketize(graphRows, filters.bucket), [graphRows, filters.bucket]);

  const kpis = useMemo(() => computeKpisFromUserChurn(userChurnRows), [userChurnRows]);

  const companyIndicators: CompanyIndicator[] = useMemo(
    () => buildCompanyIndicators(userChurnRows, companyRows),
    [userChurnRows, companyRows],
  );

  const userIndicators: UserIndicator[] = useMemo(
    () => buildUserIndicators(userChurnRows, rosterRows),
    [userChurnRows, rosterRows],
  );

  const filteredCompanies = useMemo(() => {
    let list = companyIndicators;
    if (companyTable.paidOnly) list = list.filter((c) => c.is_paid);
    if (companyTable.selectedKey) list = list.filter((c) => c.company_external_id === companyTable.selectedKey);
    return list.slice().sort((a, b) => {
      const r = rankLevel(b.risk) - rankLevel(a.risk);
      if (r !== 0) return r;
      if (a.status !== b.status) return a.status === "Churned" ? -1 : 1;
      return a.company_name.localeCompare(b.company_name);
    });
  }, [companyIndicators, companyTable]);

  const userCountryOptions = useMemo(() => {
    const codes = new Set<string>();
    for (const u of userIndicators) {
      if (u.country_code) codes.add(u.country_code);
    }
    return Array.from(codes).sort();
  }, [userIndicators]);

  const filteredUsers = useMemo(() => {
    let list = userIndicators;
    if (userTable.paidOnly) list = list.filter((u) => u.is_paid);
    if (userTable.country) list = list.filter((u) => u.country_code === userTable.country);
    if (userTable.selectedKey) list = list.filter((u) => u.user_external_id === userTable.selectedKey);
    return list.slice().sort((a, b) => {
      const r = rankLevel(b.risk) - rankLevel(a.risk);
      if (r !== 0) return r;
      if (a.status !== b.status) return a.status === "Churned" ? -1 : 1;
      return a.company_name.localeCompare(b.company_name);
    });
  }, [userIndicators, userTable]);

  const companyOptions = useMemo(
    () => companyIndicators
      .slice()
      .sort((a, b) => a.company_name.localeCompare(b.company_name))
      .map((c) => ({ key: c.company_external_id, label: c.company_name })),
    [companyIndicators],
  );

  const userOptions = useMemo(
    () => userIndicators
      .slice()
      .sort((a, b) => a.user_email.localeCompare(b.user_email))
      .map((u) => ({ key: u.user_external_id, label: `${u.user_email} (${u.company_name})` })),
    [userIndicators],
  );

  const pagePills = useMemo(() => pageFiltersToPills(filters), [filters]);

  const graphBounds = useMemo(() => {
    if (dailyRows.length === 0) {
      return { min: toISODate(addDays(today, -filters.rangeDays)), max: toISODate(today) };
    }
    let min = dailyRows[0]!.day;
    let max = dailyRows[0]!.day;
    for (const r of dailyRows) {
      if (r.day < min) min = r.day;
      if (r.day > max) max = r.day;
    }
    return { min, max };
  }, [dailyRows, today, filters.rangeDays]);

  return (
    <>
      <Topbar crumbs={["Dashboards", "Churn leading indicators"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Churn leading indicators</h1>
            <p className="lede">
              Early warning signals that track meeting, transcript, summary, and Ask Marloo engagement over time. Last-28-day values drive the headline KPIs and risk flags.
            </p>
          </div>
        </div>

        <PageFiltersBar
          filters={filters}
          onChange={setFilters}
          graphMin={graphBounds.min}
          graphMax={graphBounds.max}
        />

        {!isLoading && !error && (
          <div style={{ fontSize: 12, color: "var(--fg-3)", marginBottom: 12 }}>
            {fmtN(dailyRows.length)} daily buckets · {fmtN(userChurnRows.length)} active users · {fmtN(companyRows.length)} companies · {fmtN(rosterRows.length)} roster entries
            {dailyRows.length > 0 && (
              <> · earliest {dailyRows[0]!.day} · latest {dailyRows[dailyRows.length - 1]!.day}</>
            )}
          </div>
        )}

        {isLoading ? (
          <LoadingPanel />
        ) : error ? (
          <p style={{ color: "var(--fg-danger)" }}>{error.message}</p>
        ) : (
          <>
            <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              <KpiCard
                label="Meetings w/ transcript + summary"
                value={fmtN(kpis.distinctSummaries)}
                sub="Distinct meetings, last 28d"
                icon="ti-box"
              />
              <KpiCard
                label="Transcript to summary conversion"
                value={`${kpis.summConvRecent.toFixed(1)}%`}
                sub="Meetings-based, last 28d vs prior 28d"
                trendPct={kpis.summConvDelta}
                icon="ti-percentage"
              />
              <KpiCard
                label="Ask Marloo (Client)"
                value={fmtN(kpis.clientChats)}
                sub="User msgs, last 28d"
                icon="ti-message-circle"
              />
              <KpiCard
                label="Ask Marloo (Meeting)"
                value={fmtN(kpis.meetingChats)}
                sub="User msgs, last 28d"
                icon="ti-message-question"
              />
            </div>

            <div className="grid-2">
              <div className="card">
                <SectionCardHeader
                  title="Meetings funnel over time"
                  subtitle="Lines show raw counts on the left axis. Bars show transcript to summary conversion on the right axis (0 to 100%)."
                  filterPills={pagePills}
                />
                <div className="card-body">
                  <MeetingsFunnelChart data={funnelSeries} />
                </div>
                <FunnelTable data={funnelSeries} />
              </div>

              <div className="card">
                <SectionCardHeader
                  title="Ask Marloo engagement"
                  subtitle="User messages sent in client chats and meeting chats. Counts are per bucket in the selected graph range."
                  filterPills={pagePills}
                />
                <div className="card-body">
                  <EngagementChart data={funnelSeries} />
                </div>
                <EngagementTable data={funnelSeries} />
              </div>
            </div>

            <div className="card" style={{ marginTop: 16 }}>
              <TableFilterBar
                title="Company churn risk"
                subtitle="Risk level is derived from 28-day trend comparisons. High means 2 or more warning signals."
                filters={companyTable}
                onChange={setCompanyTable}
                options={companyOptions}
                allLabel={companyTable.paidOnly ? "All paid companies" : "All companies"}
                pickerLabel="Company"
                pagePills={pagePills}
              />
              <CompanyChurnTable rows={filteredCompanies} />
            </div>

            <div className="card" style={{ marginTop: 16 }}>
              <TableFilterBar
                title="User churn risk"
                subtitle="One row per user + company. Max tier reflects the best plan the user has ever been on."
                filters={userTable}
                onChange={setUserTable}
                options={userOptions}
                allLabel={userTable.paidOnly ? "All paid users" : "All users"}
                pickerLabel="User"
                pagePills={pagePills}
                countryOptions={userCountryOptions}
              />
              <UserChurnTable rows={filteredUsers} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

const PAGE_SIZE = 25;

function CompanyChurnTable({ rows }: { rows: CompanyIndicator[] }) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const slice = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  return (
    <>
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>Company</th>
              <th className="num">Seats (max)</th>
              <th>Status</th>
              <th>Churn risk</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((r) => (
              <tr key={r.company_external_id}>
                <td className="name-cell">{r.company_name}</td>
                <td className="num">{fmtN(r.seats_max)}</td>
                <td><StatusBadge status={r.status} /></td>
                <td><RiskBadge risk={r.risk} /></td>
                <td style={{ whiteSpace: "normal", color: "var(--fg-2)" }}>
                  {r.reasons.length > 0 ? r.reasons.join(", ") : <span className="muted">No warning signals</span>}
                </td>
              </tr>
            ))}
            {slice.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", color: "var(--fg-3)", padding: 20 }}>
                  No companies match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TableFooter
        total={rows.length}
        page={safePage}
        pages={pages}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </>
  );
}

function UserChurnTable({ rows }: { rows: UserIndicator[] }) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pages - 1);
  const slice = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  return (
    <>
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>Company</th>
              <th>Country</th>
              <th>User email</th>
              <th>Max tier</th>
              <th>Status</th>
              <th>Churn risk</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {slice.map((r) => (
              <tr key={`${r.company_external_id}-${r.user_external_id}`}>
                <td>{r.company_name}</td>
                <td>{r.country_code ?? <span className="muted">-</span>}</td>
                <td>
                  <span className="row-avatar">
                    <span className="avatar">{initialsFor(r.user_email)}</span>
                    <span>{r.user_email}</span>
                  </span>
                </td>
                <td><TierBadge tier={r.max_tier} /></td>
                <td><StatusBadge status={r.status} /></td>
                <td><RiskBadge risk={r.risk} /></td>
                <td style={{ whiteSpace: "normal", color: "var(--fg-2)" }}>
                  {r.reasons.length > 0 ? r.reasons.join(", ") : <span className="muted">No warning signals</span>}
                </td>
              </tr>
            ))}
            {slice.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", color: "var(--fg-3)", padding: 20 }}>
                  No users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TableFooter
        total={rows.length}
        page={safePage}
        pages={pages}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </>
  );
}

function TableFooter({
  total, page, pages, pageSize, onPageChange,
}: {
  total: number;
  page: number;
  pages: number;
  pageSize: number;
  onPageChange: (next: number) => void;
}) {
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 16px", borderTop: "1px solid var(--border-default)",
      fontSize: 12, color: "var(--fg-3)",
    }}>
      <span>{total === 0 ? "No rows" : `Showing ${start}-${end} of ${total.toLocaleString()}`}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={page === 0}
          onClick={() => onPageChange(Math.max(0, page - 1))}
        >
          <i className="ti ti-chevron-left" />
        </button>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {page + 1} of {pages}
        </span>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={page >= pages - 1}
          onClick={() => onPageChange(Math.min(pages - 1, page + 1))}
        >
          <i className="ti ti-chevron-right" />
        </button>
      </div>
    </div>
  );
}
